import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Printer,
  Download, Upload, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Planning components
import { weekDates, monthDates, fmtDate, fmtDay, getISOWeek, DAY_NAMES, exportToExcel } from '../components/planning/helpers';
import CalendarPicker from '../components/planning/CalendarPicker';
import KPICards from '../components/planning/KPICards';
import LineFilter from '../components/planning/LineFilter';
import CalCell from '../components/planning/CalCell';
import DayDetailPanel from '../components/planning/DayDetailPanel';
import CreatePlanModal from '../components/planning/CreatePlanModal';
import UpdateModal from '../components/planning/UpdateModal';
import RescheduleModal from '../components/planning/RescheduleModal';
import ImportModal from '../components/planning/ImportModal';
import WeeklyPlan from './reports/WeeklyPlan';
import { printDailySheet } from '../components/planning/printDailySheet';

const API = process.env.REACT_APP_BACKEND_URL;

// ============= MAIN PAGE =============
export default function ProductionPlanning({ isAdmin = true, targetDate, onTargetDateConsumed }) {
  const [lines, setLines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [plans, setPlans] = useState([]);
  const [components, setComponents] = useState({});
  const [view, setView] = useState(() => localStorage.getItem('pp_view') || 'weekly');
  const [currentDate, setCurrentDate] = useState(() => localStorage.getItem('pp_date') || new Date().toISOString().split('T')[0]);

  useEffect(() => { localStorage.setItem('pp_view', view); }, [view]);
  useEffect(() => { localStorage.setItem('pp_date', currentDate); }, [currentDate]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [reschedulingPlan, setReschedulingPlan] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [mdpData, setMdpData] = useState({});
  const [periodMdp, setPeriodMdp] = useState({});
  const [mappings, setMappings] = useState({});
  const [kpiCollapsed, setKpiCollapsed] = useState(false);
  const calendarRef = useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (showCreate || showImport || editingPlan || reschedulingPlan) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Navigate to target date from other pages (e.g. Reports)
  useEffect(() => {
    if (targetDate) {
      setCurrentDate(targetDate);
      setView('weekly');
      if (onTargetDateConsumed) onTargetDateConsumed();
    }
  }, [targetDate, onTargetDateConsumed]);


  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  // Fetch planning-specific data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [linesRes, projectsRes] = await Promise.all([
          axios.get(`${API}/api/lines`),
          axios.get(`${API}/api/planning/projects`),
        ]);
        setLines(linesRes.data);
        setProjects(projectsRes.data);
        if (linesRes.data.length > 0) {
          setSelectedLines(prev => prev.length > 0 ? prev : linesRes.data.map(l => l.id));
        }

        // Parallel: sync + fetch components for ALL projects at once
        await Promise.all(projectsRes.data.map(proj =>
          axios.post(`${API}/api/planning/projects/${proj.id}/sync-components`).catch(() => {})
        ));
        const compResults = await Promise.all(projectsRes.data.map(proj =>
          axios.get(`${API}/api/planning/projects/${proj.id}/components`).catch(() => ({ data: [] }))
        ));
        const compMap = {};
        projectsRes.data.forEach((proj, i) => { compMap[proj.id] = compResults[i].data; });
        setComponents(compMap);

        // Parallel: fetch MDP data for seat-based projects
        const seatProjects = projectsRes.data.filter(p => p.planning_unit === 'seat');
        const mdpResults = await Promise.all(seatProjects.map(proj =>
          Promise.all([
            axios.get(`${API}/api/mdp/project-summary/${proj.id}`).catch(() => ({ data: {} })),
            axios.get(`${API}/api/mdp/mappings?project_id=${proj.id}`).catch(() => ({ data: [] })),
          ])
        ));

        const mdpMap = {};
        const mappingsMap = {};
        seatProjects.forEach((proj, i) => {
          const [mdpRes, mappRes] = mdpResults[i];
          if (mdpRes.data?.has_mdp_data) {
            mdpMap[proj.id] = mdpRes.data.mdp_by_description || {};
          }
          const descMap = {};
          const ksuToTcMap = {};
          (mappRes.data || []).forEach(m => {
            const desc = m.customer_material_description;
            if (desc) {
              descMap[desc] = { tc: m.customer_material_code || '', ksu: m.kiel_material_code || '' };
            }
            if (m.kiel_material_code && m.customer_material_code) {
              ksuToTcMap[m.kiel_material_code] = m.customer_material_code;
            }
          });
          descMap._ksuToTc = ksuToTcMap;
          mappingsMap[proj.id] = descMap;
        });
        setMdpData(mdpMap);
        setMappings(mappingsMap);
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  const dateRange = useMemo(() => {
    if (view === 'daily') return [currentDate];
    if (view === 'weekly') return weekDates(currentDate);
    return monthDates(currentDate).map(d => d.date || d);
  }, [view, currentDate]);

  const rangeStart = dateRange[0];
  const rangeEnd = dateRange[dateRange.length - 1];

  // Fetch period-specific MDP data - reusable callback
  const fetchPeriodMdp = useCallback(async () => {
    if (!rangeStart || !rangeEnd || projects.length === 0) return;
    const seatProjects = projects.filter(p => p.planning_unit === 'seat');
    const results = await Promise.all(seatProjects.map(proj => {
      let url = `${API}/api/mdp/period-data?project_id=${proj.id}&start_date=${rangeStart}&end_date=${rangeEnd}`;
      if (selectedDay) url += `&cumul_date=${selectedDay}`;
      return axios.get(url).catch(() => ({ data: {} }));
    }));
    const pMap = {};
    seatProjects.forEach((proj, i) => { pMap[proj.id] = results[i].data; });
    setPeriodMdp(pMap);
  }, [rangeStart, rangeEnd, projects, selectedDay]);

  useEffect(() => { fetchPeriodMdp(); }, [fetchPeriodMdp]);

  const fetchPlans = useCallback(async () => {
    try {
      const params = new URLSearchParams({ start_date: rangeStart, end_date: rangeEnd });
      const res = await axios.get(`${API}/api/plans?${params}`);
      const projectIds = new Set(projects.map(p => p.id));
      const validPlans = res.data.filter(p => projectIds.has(p.project_id));
      if (validPlans.length < res.data.length) {
        axios.delete(`${API}/api/plans/cleanup-orphans`).catch(() => {});
      }
      setPlans(validPlans);
    } catch (err) { console.error(err); }
  }, [rangeStart, rangeEnd, projects]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const refreshComponents = useCallback(async () => {
    const results = await Promise.all(projects.map(proj =>
      axios.get(`${API}/api/planning/projects/${proj.id}/components`).catch(() => ({ data: [] }))
    ));
    const compMap = {};
    projects.forEach((proj, i) => { compMap[proj.id] = results[i].data; });
    setComponents(compMap);
  }, [projects]);

  const filteredPlans = useMemo(() => {
    return plans.filter(p => selectedLines.includes(p.line_id));
  }, [plans, selectedLines]);

  const plansByDate = useMemo(() => {
    const map = {};
    filteredPlans.forEach(p => {
      if (!map[p.date]) map[p.date] = [];
      map[p.date].push(p);
    });
    return map;
  }, [filteredPlans]);

  const toggleLine = (lineId) => {
    setSelectedLines(prev =>
      prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]
    );
  };

  const navigate = (dir) => {
    const d = new Date(currentDate + 'T12:00:00');
    if (view === 'daily') d.setDate(d.getDate() + dir);
    else if (view === 'weekly') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const today = new Date().toISOString().split('T')[0];

  const headerLabel = useMemo(() => {
    const d = new Date(currentDate + 'T00:00:00');
    if (view === 'daily') {
      const wk = getISOWeek(currentDate);
      return `${d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} (W${wk})`;
    }
    if (view === 'weekly') {
      const dates = weekDates(currentDate);
      const start = new Date(dates[0] + 'T00:00:00');
      const end = new Date(dates[5] + 'T00:00:00');
      const wk = getISOWeek(dates[0]);
      return `W${wk}: ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  }, [view, currentDate]);

  const handleCreate = async (form) => {
    if (form._skipDefault) {
      // City Bus already created via create-citybus-sets
      setShowCreate(false);
      fetchPlans();
      return;
    }
    try {
      const res = await axios.post(`${API}/api/plans/create-day-template`, form);
      toast.success('Day template created');
      if (res.data?.warning) {
        toast.warning(res.data.warning, { duration: 6000 });
      }
      setShowCreate(false);
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleUpdate = async (planId, updates) => {
    try {
      await axios.put(`${API}/api/plans/${planId}`, updates);
      toast.success('Updated');
      setEditingPlan(null);
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error('Error'); }
  };

  const handleQuickEdit = async (planId, updates) => {
    try {
      await axios.put(`${API}/api/plans/${planId}`, updates);
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error('Error updating quantity'); }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      const res = await axios.delete(`${API}/api/plans/${planId}`);
      if (res.data.restored) {
        toast.success(`Deleted. Components restored to ${res.data.restored.source_date}`, { duration: 4000 });
      } else {
        toast.success('Deleted');
      }
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error('Error'); }
  };

  const handleRemoveProject = async (projectId, date) => {
    const proj = projects.find(p => p.id === projectId);
    if (!window.confirm(`Remove "${proj?.name || 'this project'}" from ${date}? All plan entries will be deleted.`)) return;
    try {
      const res = await axios.delete(`${API}/api/plans/by-project-day?project_id=${projectId}&date=${date}`);
      toast.success(`Removed ${res.data.count} entries`);
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error('Error removing project'); }
  };

  const handleReschedule = async (planId, newDate) => {
    try {
      await axios.post(`${API}/api/plans/${planId}/reschedule`, { new_date: newDate });
      toast.success('Rescheduled');
      setReschedulingPlan(null);
      fetchPlans();
      refreshComponents();
      fetchPeriodMdp();
    } catch (err) { toast.error('Error'); }
  };

  const onDayClick = (date) => {
    if (view === 'daily') return;
    setSelectedDay(date);
  };

  // Weekly grid
  const renderWeekly = () => {
    const dates = weekDates(currentDate);
    return (
      <div className="grid grid-cols-6 gap-2" data-testid="weekly-view">
        {dates.map((date, i) => (
          <div key={date}>
            <div className="text-center mb-1">
              <span className="text-[10px] text-slate-400 font-medium">{DAY_NAMES[i]}</span>
            </div>
            <CalCell date={date} plans={plansByDate[date] || []} isToday={date === today} isSelected={date === selectedDay} onClick={onDayClick} onAddPlan={isAdmin ? (d) => { setCreateDate(d); setShowCreate(true); } : null} projects={projects} />
          </div>
        ))}
      </div>
    );
  };

  // Monthly grid
  const renderMonthly = () => {
    const dates = monthDates(currentDate);
    return (
      <div data-testid="monthly-view">
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="bg-slate-50 text-center py-1.5 text-[10px] font-semibold text-slate-500">{d}</div>
          ))}
          {dates.map(d => {
            const dayPlans = plansByDate[d.date] || [];
            const isToday = d.date === today;
            const isSelected = d.date === selectedDay;
            return (
              <div key={d.date} onClick={() => onDayClick(d.date)}
                className={`bg-white min-h-[70px] p-1 cursor-pointer hover:bg-slate-50 ${!d.cur ? 'opacity-30' : ''} ${isToday && isSelected ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30' : isSelected ? 'ring-2 ring-indigo-400 ring-inset bg-indigo-50/30' : isToday ? 'ring-2 ring-blue-400 ring-inset' : ''}`}>
                <span className={`text-[10px] font-medium ${isToday ? 'text-blue-600 font-bold' : isSelected ? 'text-indigo-600 font-bold' : 'text-slate-600'}`}>{fmtDay(d.date)}</span>
                <div className="space-y-0.5 mt-0.5">
                  {Object.entries(dayPlans.reduce((acc, p) => {
                    if (!acc[p.project_id]) acc[p.project_id] = { name: p.project_name, planned: 0, unit: p.planning_unit };
                    acc[p.project_id].planned += p.planned_quantity || 0;
                    return acc;
                  }, {})).filter(([, v]) => v.planned > 0).slice(0, 2).map(([pid, v]) => (
                    <div key={pid} className="text-[8px] bg-slate-100 rounded px-1 py-0.5 truncate">
                      <span className="font-semibold">{v.name}</span> {v.planned} {v.unit === 'seat' ? 's' : v.unit === 'set' ? 'set' : ''}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Daily view
  const renderDaily = () => {
    const dayPlans = plansByDate[currentDate] || [];
    if (dayPlans.length === 0 && Object.keys(components).length === 0) {
      return (
        <div className="text-center py-16 text-slate-400" data-testid="daily-view-empty">
          <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No plans for {fmtDate(currentDate)}</p>
          {isAdmin && (
            <button onClick={() => { setCreateDate(currentDate); setShowCreate(true); }}
              className="mt-3 px-4 py-2 text-xs bg-slate-800 text-white rounded-lg hover:bg-slate-900">
              <Plus className="w-3 h-3 inline mr-1" /> Add Plan
            </button>
          )}
        </div>
      );
    }
    return (
      <DayDetailPanel date={currentDate} plans={dayPlans} projects={projects} components={components} mdpData={mdpData} periodMdp={periodMdp} lines={lines} mappings={mappings}
        onClose={() => {}} onUpdate={isAdmin ? setEditingPlan : null} onDelete={isAdmin ? handleDelete : null} onReschedule={isAdmin ? setReschedulingPlan : null} onQuickEdit={isAdmin ? handleQuickEdit : null} onRemoveProject={isAdmin ? handleRemoveProject : null}
        onAddPlan={isAdmin ? (d) => { setCreateDate(d); setShowCreate(true); } : null} onRefreshPlans={fetchPlans} isAdmin={isAdmin} />
    );
  };

  return (
    <div className="space-y-4" data-testid="production-planning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Production Planning</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Today: <span className="font-semibold text-blue-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}</span>
            <span className="mx-1.5 text-slate-300">|</span>
            <span className="font-semibold text-slate-700">W{getISOWeek(today)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setShowImport(true)}
              className="px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
              data-testid="import-btn">
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
          )}
          <button onClick={() => { exportToExcel(filteredPlans, dateRange); toast.success('Plan exported as CSV'); }}
            className="px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
            data-testid="export-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button onClick={async () => {
              const printDate = selectedDay || currentDate;
              const dayPlans = plansByDate[printDate] || [];
              if (dayPlans.length === 0) { toast.error('No plans for selected day'); return; }
              // Fetch Hitachi production data for print
              const hitachiPlans = dayPlans.filter(p => p.is_hitachi && p.set_number && p.status !== 'rescheduled');
              let hitachiData = null;
              if (hitachiPlans.length > 0) {
                hitachiData = {};
                for (const hp of hitachiPlans) {
                  const pid = hp.project_id;
                  if (!hitachiData[pid]) {
                    try {
                      const cfgRes = await axios.get(`${API}/api/hitachi/config/${pid}`);
                      hitachiData[pid] = { seatTypes: cfgRes.data.seat_types || [], production: {} };
                    } catch { hitachiData[pid] = { seatTypes: [], production: {} }; }
                  }
                  try {
                    const prodRes = await axios.get(`${API}/api/hitachi/production/${pid}/${hp.set_number}/${printDate}`);
                    hitachiData[pid].production[hp.set_number] = prodRes.data;
                  } catch { /* ignore */ }
                }
              }
              printDailySheet(printDate, dayPlans, lines, projects, components, periodMdp, hitachiData);
            }}
            className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
            data-testid="print-daily-sheet-btn">
            <Printer className="w-3.5 h-3.5" /> Print Daily Sheet
          </button>
          {isAdmin && (
            <button onClick={() => { setCreateDate(null); setShowCreate(true); }}
              className="px-4 py-2 text-xs font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-900 flex items-center gap-1.5"
              data-testid="new-plan-btn">
              <Plus className="w-3.5 h-3.5" /> New Plan
            </button>
          )}
        </div>
      </div>

      {/* Line Filter */}
      <div className="flex items-center justify-between">
        <LineFilter lines={lines} selectedLines={selectedLines} onToggle={toggleLine} />
        <div className="flex items-center gap-2 relative">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {['daily', 'weekly', 'monthly'].map(v => (
              <button key={v} onClick={() => { setView(v); setSelectedDay(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${view === v ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`view-${v}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="nav-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => { setCurrentDate(today); setSelectedDay(null); }} className="px-3 py-1.5 text-xs font-medium hover:bg-slate-100 rounded-lg" data-testid="nav-today">Today</button>
            <button onClick={() => navigate(1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="nav-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm font-semibold text-slate-700 ml-1 cursor-pointer hover:text-blue-600 hover:underline transition-colors relative"
            onClick={() => setShowCalendar(!showCalendar)} data-testid="date-header-label">
            {headerLabel}
            <CalendarDays className="w-3.5 h-3.5 inline ml-1.5 opacity-50" />
          </span>
          {showCalendar && (
            <div ref={calendarRef} className="absolute top-full right-0 mt-1 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 p-3 w-[280px]" data-testid="calendar-popup">
              <CalendarPicker currentDate={currentDate} onSelect={(date) => { setCurrentDate(date); setSelectedDay(null); setShowCalendar(false); }} />
            </div>
          )}
        </div>
      </div>

      {/* KPIs - Collapsible */}
      <div data-testid="kpi-section">
        <button onClick={() => setKpiCollapsed(c => !c)}
          className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors mb-1"
          data-testid="kpi-toggle-btn">
          <span className="text-xs font-semibold text-slate-600">KPI Summary — {headerLabel}</span>
          {kpiCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
        </button>
        {!kpiCollapsed && (
          <KPICards filteredPlans={filteredPlans} projects={projects} components={components} mdpData={mdpData} periodMdp={periodMdp} weekLabel={headerLabel} />
        )}
      </div>

      {/* Calendar */}
      {view === 'weekly' ? renderWeekly() : view === 'monthly' ? renderMonthly() : renderDaily()}

      {/* Day Detail Panel */}
      {selectedDay && view !== 'daily' && (
        <DayDetailPanel date={selectedDay} plans={plansByDate[selectedDay] || []} projects={projects} components={components} mdpData={mdpData} periodMdp={periodMdp} lines={lines} mappings={mappings}
          onClose={() => setSelectedDay(null)} onUpdate={isAdmin ? setEditingPlan : null} onDelete={isAdmin ? handleDelete : null} onReschedule={isAdmin ? setReschedulingPlan : null} onQuickEdit={isAdmin ? handleQuickEdit : null} onRemoveProject={isAdmin ? handleRemoveProject : null}
          onAddPlan={isAdmin ? (d) => { setCreateDate(d); setShowCreate(true); } : null} onRefreshPlans={fetchPlans} isAdmin={isAdmin} />
      )}

      {/* City Bus Consolidated Material Demand — bottom of the weekly view */}
      {view === 'weekly' && (
        <WeeklyPlan embedded initialWeekStart={weekDates(currentDate)[0]} />
      )}

      {/* Modals */}
      {showCreate && <CreatePlanModal onClose={() => setShowCreate(false)} onSave={handleCreate} lines={lines} projects={projects} initialDate={createDate || selectedDay} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); fetchPlans(); refreshComponents(); }} lines={lines} projects={projects} />}
      {editingPlan && <UpdateModal plan={editingPlan} onClose={() => setEditingPlan(null)} onSave={handleUpdate} />}
      {reschedulingPlan && <RescheduleModal plan={reschedulingPlan} onClose={() => setReschedulingPlan(null)} onSave={handleReschedule} />}
    </div>
  );
}
